import {
  CheckoutCustomer,
  CheckoutDeliveryMethod,
  CheckoutContext,
  CheckoutDeliveryPayload,
  CheckoutDeliveryReceiver,
  CheckoutDeliveryRequest,
  ClearDeliveryRequest,
  DeliveryMachineLocation,
  CheckoutError,
  CheckoutOrder,
  CheckoutOrderProductRequest,
  CheckoutPaymentMethod,
  CheckoutRequest,
  CheckoutResponse,
  DeliveryMethodTiming,
  DeliveryMethodTimingWeekdayApi,
  OrderPayRequest,
} from "./checkoutTypes";

export type BuyerFormData = {
  type?: string;
  firstname?: string;
  surname?: string;
  phone?: string;
  email?: string;
  language?: string;
  country_code?: string;
  organization_country_code?: string;
  organization_name?: string;
  organization_reg_nr?: string;
  organization_KMKR_number?: string;
  organization_phone?: string;
  organization_email?: string;
  organization_address?: string;
  representativeFirstname?: string;
  representativeSurname?: string;
};

export type CartItemForOrderApi = {
  productUuid?: string;
  quantity: number;
  eventHash?: string;
  cartLineSource?: "autoExtra" | "eventAdditional";
  parentUuid?: string | null;
  parentProductUuids?: string[];
  invalid?: boolean;
  addVariationProductProductSelectedVariationData?: Record<string, unknown> | string;
};

function normalizeParentProductUuids(
  parentProductUuids: Array<string | null | undefined> | undefined
): string[] | undefined {
  if (!parentProductUuids?.length) {
    return undefined;
  }
  const normalized = [
    ...new Set(
      parentProductUuids.filter(
        (uuid) => typeof uuid === "string" && uuid.trim().length > 0
      )
    ),
  ];
  return normalized.length > 0 ? normalized as string[] : undefined;
}

function orderRequestParentProductUuids(
  item: CartItemForOrderApi
): string[] | undefined {
  const normalizedParents = normalizeParentProductUuids(
    item.parentProductUuids
  );
  const isExtraLine =
    item.cartLineSource === "autoExtra" ||
    (normalizedParents?.length ?? 0) > 0;
  if (!isExtraLine) {
    return undefined;
  }
  return normalizedParents;
}

function invalidAutoExtraLine(item: CartItemForOrderApi): boolean {
  if (item.invalid !== true) {
    return false;
  }
  return (
    item.cartLineSource === "autoExtra" ||
    (normalizeParentProductUuids(item.parentProductUuids)?.length ?? 0) > 0
  );
}

export function mapCartItemsToOrderProducts(
  items: CartItemForOrderApi[]
): CheckoutOrderProductRequest[] {
  return items
    .filter(
      (item) =>
        typeof item.productUuid === "string" &&
        item.productUuid.length > 0 &&
        Number.isFinite(item.quantity) &&
        !invalidAutoExtraLine(item)
    )
    .map((item) => {
      const parentProductUuids = orderRequestParentProductUuids(item);
      return {
        productUuid: item.productUuid!,
        quantity: item.quantity,
        ...(item.eventHash ? { eventHash: item.eventHash } : {}),
        ...(parentProductUuids ? { parentProductUuids } : {}),
        ...(item.addVariationProductProductSelectedVariationData
          ? {
              addVariationProductProductSelectedVariationData:
                item.addVariationProductProductSelectedVariationData,
            }
          : {}),
      };
    });
}

export function mapBuyerToCustomer(
  buyer?: BuyerFormData,
  options?: { includeB2bContactPerson?: boolean }
): CheckoutCustomer | undefined {
  if (!buyer) return undefined;
  const includeB2bContactPerson = options?.includeB2bContactPerson === true;
  if (buyer.type === "organization") {
    if (!buyer.organization_name || !buyer.organization_reg_nr) {
      return undefined;
    }
    const representativeFirstname = buyer.representativeFirstname?.trim();
    const representativeSurname = buyer.representativeSurname?.trim();
    const hasCompleteContact =
      includeB2bContactPerson &&
      !!representativeFirstname &&
      !!representativeSurname;
    return {
      legalEntity: {
        legalName: buyer.organization_name,
        regNumber: buyer.organization_reg_nr,
        vatNumber: buyer.organization_KMKR_number || undefined,
        address: buyer.organization_address || undefined,
        phone: buyer.organization_phone || "",
        email: buyer.organization_email || "",
        language: buyer.language,
        countryCode: buyer.organization_country_code || buyer.country_code,
        ...(hasCompleteContact
          ? {
              contactFirstname: representativeFirstname,
              contactSurname: representativeSurname,
            }
          : {}),
      },
    };
  }
  if (!buyer.firstname || !buyer.surname || !buyer.phone || !buyer.email) {
    return undefined;
  }
  return {
    person: {
      firstname: buyer.firstname,
      surname: buyer.surname,
      phone: buyer.phone,
      email: buyer.email,
      language: buyer.language,
      countryCode: buyer.country_code,
    },
  };
}

export type DeliveryFormData = {
  methodUuid?: string;
  /** @deprecated Prefer methodUuid. */
  uuid?: string;
  address?: string;
  city?: string;
  postalCode?: string;
  parcelMachineLocation?: DeliveryMachineLocation;
  parcelMachineName?: string;
  parcelDescription?: string;
  requestedDeliveryDateTime?: string;
  receiverEmail?: string;
  receiverFirstname?: string;
  receiverSurname?: string;
  receiverPhone?: string;
  receiverCountryCode?: string;
  buyerIsReceiver?: boolean;
  buyerAddress?: string;
  buyerCity?: string;
  buyerPostalCode?: string;
};

export function isClearDeliveryInput(
  delivery: unknown
): delivery is ClearDeliveryRequest {
  return (
    !!delivery &&
    typeof delivery === "object" &&
    "clear" in delivery &&
    (delivery as ClearDeliveryRequest).clear === true
  );
}

export function mapDeliveryInputToPayload(
  delivery: DeliveryFormData | ClearDeliveryRequest | null | undefined
): CheckoutDeliveryPayload | undefined {
  if (typeof delivery === "undefined") {
    return undefined;
  }
  if (delivery === null || isClearDeliveryInput(delivery)) {
    return { clear: true };
  }
  const methodUuid = delivery.methodUuid ?? delivery.uuid ?? "";
  return mapDeliveryFormToRequest(delivery, methodUuid);
}

export function mapDeliveryFormToRequest(
  form: DeliveryFormData,
  methodUuid: string
): CheckoutDeliveryRequest {
  const hasLocker = !!form.parcelMachineLocation?.machineLocation;

  const receiver: CheckoutDeliveryReceiver = {
    isReceiver: !!form.buyerIsReceiver,
    countryCode: form.receiverCountryCode ?? "EE",
    firstname: form.receiverFirstname ?? "",
    surname: form.receiverSurname ?? "",
    phone: form.receiverPhone ?? "",
    email: form.receiverEmail ?? "",
  };

  if (!hasLocker) {
    receiver.address = form.buyerAddress ?? form.address ?? "";
    receiver.city = form.buyerCity ?? form.city ?? "";
    receiver.postalCode = form.buyerPostalCode ?? form.postalCode ?? "";
  }

  const request: CheckoutDeliveryRequest = {
    uuid: methodUuid,
    address: form.address ?? "",
    city: form.city ?? "",
    postalCode: form.postalCode ?? "",
    parcelMachineName: form.parcelMachineName ?? "",
    parcelDescription: form.parcelDescription ?? "",
    requestedDeliveryDateTime: form.requestedDeliveryDateTime,
    receiver,
  };

  if (hasLocker) {
    request.parcelMachineLocation = form.parcelMachineLocation;
  }

  return request;
}

export function buildSyncOrderRequest(args: {
  orderUuid?: string | null;
  orderProducts: CheckoutOrderProductRequest[];
  customer?: CheckoutCustomer;
  delivery?: CheckoutDeliveryPayload;
  infoCustomer?: string;
  checkoutContext?: CheckoutContext;
}): CheckoutRequest {
  return {
    ...(args.orderUuid ? { order: { uuid: args.orderUuid } } : {}),
    orderProducts: args.orderProducts,
    ...(args.customer ? { customer: args.customer } : {}),
    ...(typeof args.delivery !== "undefined" ? { delivery: args.delivery } : {}),
    ...(args.infoCustomer ? { infoCustomer: args.infoCustomer } : {}),
    ...(args.checkoutContext ? { checkoutContext: args.checkoutContext } : {}),
  };
}

export function buildOrderPayRequest(args: {
  orderUuid: string;
  paymentMethod: Pick<
    CheckoutPaymentMethod,
    "uuid" | "maksekeskusPaymentMethodName"
  >;
  checkoutUrl?: string | null;
}): OrderPayRequest {
  const maksekeskusPaymentMethodName =
    typeof args.paymentMethod.maksekeskusPaymentMethodName === "string" &&
    args.paymentMethod.maksekeskusPaymentMethodName.trim().length > 0
      ? args.paymentMethod.maksekeskusPaymentMethodName
      : undefined;

  return {
    order: { uuid: args.orderUuid },
    paymentMethod: {
      uuid: args.paymentMethod.uuid,
      ...(maksekeskusPaymentMethodName
        ? { maksekeskusPaymentMethodName }
        : {}),
    },
    ...(args.checkoutUrl ? { checkout_url: args.checkoutUrl } : {}),
  };
}

const NON_BLOCKING_ORDER_ERROR_CODES = new Set(["productUuidNotResolvable"]);

export function hasOrderNotEditableError(
  errors: CheckoutError[] | undefined
): boolean {
  return !!errors?.some((error) => error.code === "orderNotEditable");
}

export function hasOrderApiErrors(data: CheckoutResponse | undefined): boolean {
  if (!data?.errors?.length) {
    return false;
  }
  const blocking = data.errors.filter(
    (error) => !NON_BLOCKING_ORDER_ERROR_CODES.has(error.code)
  );
  if (blocking.length > 0) {
    return true;
  }
  return !data.order;
}

function fromValidationErrors(data: unknown): CheckoutError[] {
  if (!data || typeof data !== "object") return [];
  const rawErrors = (data as { errors?: Record<string, string[]> }).errors;
  if (!rawErrors || typeof rawErrors !== "object") return [];
  return Object.entries(rawErrors).flatMap(([field, messages]) =>
    (messages || []).map((message) => ({
      code: "validationFailed",
      message: `${field}: ${message}`,
    }))
  );
}

export function formatOrderApiErrors(data: unknown): CheckoutError[] {
  if (!data || typeof data !== "object") return [];
  const errors = (data as { errors?: unknown }).errors;
  if (Array.isArray(errors)) {
    return errors
      .filter((entry) => entry && typeof entry === "object")
      .map((entry) => {
        const err = entry as CheckoutError;
        return {
          code: err.code || "unknown",
          message: err.message || "Unknown checkout error",
          ...(err.details ? { details: err.details } : {}),
          ...(err.productUuid ? { productUuid: err.productUuid } : {}),
          ...(typeof err.cartItemIndex === "number"
            ? { cartItemIndex: err.cartItemIndex }
            : {}),
        };
      });
  }
  return fromValidationErrors(data);
}

export function extractOrderApiMessage(data: unknown): string | undefined {
  const formatted = formatOrderApiErrors(data);
  if (formatted.length === 0) return undefined;
  return formatted.map((error) => error.message).join(" ");
}

function normalizePaymentMethod(method: CheckoutPaymentMethod): CheckoutPaymentMethod {
  return method;
}

const WEEKDAY_CODE_TO_JS_DAY: Record<string, number> = {
  sun: 0,
  mon: 1,
  tue: 2,
  wed: 3,
  thu: 4,
  fri: 5,
  sat: 6,
};

/** Strip timezone offset from API times (e.g. `00:00:00+03:00` → `00:00:00`). */
export function parseDeliveryTimingTime(time: string | undefined): string | undefined {
  if (!time) return undefined;
  const match = time.match(/^(\d{2}):(\d{2})(?::(\d{2}))?/);
  if (!match) return time;
  const [, hours, minutes, seconds] = match;
  return seconds ? `${hours}:${minutes}:${seconds}` : `${hours}:${minutes}`;
}

export function weekdayCodeToJsDay(weekday: string): number | undefined {
  const key = weekday.trim().toLowerCase();
  const day = WEEKDAY_CODE_TO_JS_DAY[key];
  return typeof day === "number" ? day : undefined;
}

export function mapTimingWeekdaysToDeliveryMethodTimings(
  weekdays: DeliveryMethodTimingWeekdayApi[]
): DeliveryMethodTiming[] {
  return weekdays.flatMap((entry) => {
    const weekday = weekdayCodeToJsDay(entry.weekday);
    if (weekday === undefined) return [];

    const timing: DeliveryMethodTiming = {
      weekday,
      active: 1,
      start: parseDeliveryTimingTime(entry.fromTime),
      end: parseDeliveryTimingTime(entry.toTime),
      interval: entry.interval,
      minDays: entry.minDays,
      maxDays: entry.maxDays,
    };

    // Legacy checkout paths also match Sunday as weekday 7.
    if (weekday === 0) {
      return [timing, { ...timing, weekday: 7 }];
    }

    return [timing];
  });
}

function hasTimedDelivery(method: CheckoutDeliveryMethod): boolean {
  const apiWeekdays = method.timing?.weekdays;
  if (apiWeekdays && apiWeekdays.length > 0) return true;
  return !!(method.deliveryMethodTimings && method.deliveryMethodTimings.length > 0);
}

export function normalizeDeliveryMethod(
  method: CheckoutDeliveryMethod
): CheckoutDeliveryMethod {
  const apiWeekdays = method.timing?.weekdays;
  const deliveryMethodTimings =
    apiWeekdays && apiWeekdays.length > 0
      ? mapTimingWeekdaysToDeliveryMethodTimings(apiWeekdays)
      : method.deliveryMethodTimings;

  const { timing: _timing, ...rest } = method;

  return {
    ...rest,
    ...(deliveryMethodTimings?.length
      ? { deliveryMethodTimings }
      : {}),
    timingRequired: rest.timingRequired ?? hasTimedDelivery(method),
  };
}

function normalizeOrder(order: CheckoutOrder): CheckoutOrder {
  return {
    ...order,
    orderProducts: order.orderProducts ?? [],
  };
}

export function normalizeOrderApiResponse(data: CheckoutResponse): CheckoutResponse {
  return {
    ...data,
    ...(data.order ? { order: normalizeOrder(data.order) } : {}),
    ...(data.paymentMethods
      ? { paymentMethods: data.paymentMethods.map(normalizePaymentMethod) }
      : {}),
    ...(data.deliveryMethods
      ? { deliveryMethods: data.deliveryMethods.map(normalizeDeliveryMethod) }
      : {}),
    ...(data.paymentMethod ? { paymentMethod: normalizePaymentMethod(data.paymentMethod) } : {}),
  };
}
